// @djsem/analysis-contract — Booking CRM Lite (51) + Venue Marketplace (54)

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 51 — Booking / Promoter / Venue CRM Lite
// ============================================================================

/** Lifecycle status of a booking, from initial lead through completion/archival. */
export type DJSEMBookingStatus =
  | "lead" | "enquiry" | "quoted" | "tentative" | "confirmed"
  | "prep_in_progress" | "ready" | "completed" | "cancelled" | "archived";

/** The role a CRM contact plays relative to a booking/venue. */
export type DJSEMContactType =
  | "promoter" | "venue_manager" | "private_client" | "agent"
  | "sound_engineer" | "visual_operator" | "lighting_operator"
  | "photographer" | "press" | "other";

/** Status of a single asset needed for a booking (e.g. rider, promo image). */
export type DJSEMBookingAssetStatus =
  | "not_needed" | "needed" | "in_progress" | "ready" | "sent" | "approved" | "rejected";

/** A CRM contact record (promoter, venue manager, crew, etc). */
export interface DJSEMCrmContact {
  id: string;
  displayName: string;
  contactType: DJSEMContactType;
  companyName?: string;
  email?: string; phone?: string; website?: string;
  socialLinks: Array<{ platform: string; url: string }>;
  notes?: string;
  tags: string[];
  status: "active" | "archived";
  createdAt: string; updatedAt: string;
}

/** A CRM record for a venue, independent of (but linkable to) a full venue profile. */
export interface DJSEMVenueCrmRecord {
  id: string;
  venueProfileId?: string;
  name: string;
  address?: string; city?: string; country?: string;
  capacity?: number;
  contactIds: string[];
  technicalNotes?: string;
  restrictions: string[];
  createdAt: string; updatedAt: string;
}

/** A single item on a booking's required-asset checklist. */
export interface DJSEMBookingAssetChecklistItem {
  id: string;
  label: string;
  assetType: string;
  linkedAssetId?: string;
  status: DJSEMBookingAssetStatus;
  sentAt?: string;
  warnings: string[];
}

/** A post-booking follow-up task (e.g. send recap, thank promoter). */
export interface DJSEMBookingFollowUpTask {
  id: string;
  title: string;
  dueDate?: string;
  linkedResourceType?: string;
  linkedResourceId?: string;
  status: "todo" | "in_progress" | "done" | "skipped";
  createdAt: string;
}

/** A single gig/event booking record, linking a venue, contacts, and prep artifacts (event brief, gig pack, rider). */
export interface DJSEMBookingRecord {
  id: string;
  projectId?: string;
  title: string;
  eventType: string;
  status: DJSEMBookingStatus;
  date?: string;
  venueCrmRecordId?: string;
  contactIds: string[];
  eventBriefId?: string;
  gigPackId?: string;
  technicalRiderExportId?: string;
  clientPreviewId?: string;
  feeNotes?: string;
  assetChecklist: DJSEMBookingAssetChecklistItem[];
  followUpTasks: DJSEMBookingFollowUpTask[];
  notes?: string;
  warnings: string[];
  createdAt: string; updatedAt: string;
}

// Legal transitions
const BOOKING_TRANSITIONS: Record<DJSEMBookingStatus, DJSEMBookingStatus[]> = {
  lead: ["enquiry", "cancelled", "archived"],
  enquiry: ["quoted", "tentative", "cancelled", "archived"],
  quoted: ["tentative", "confirmed", "cancelled", "archived"],
  tentative: ["confirmed", "cancelled", "archived"],
  confirmed: ["prep_in_progress", "cancelled", "archived"],
  prep_in_progress: ["ready", "cancelled", "archived"],
  ready: ["completed", "cancelled", "archived"],
  completed: ["archived"],
  cancelled: ["archived"],
  archived: [],
};

/** Input for {@link DJSEMBookingService.createBooking}. */
export interface CreateBookingInput {
  projectId?: string; title: string; eventType: string; date?: string;
  contactIds?: string[]; feeNotes?: string;
}

/** Manages CRM contact records (promoters, venue managers, crew). */
export class DJSEMCrmContactService {
  /**
   * Create a new active CRM contact.
   * @param input - the contact's fields (excluding id/status/timestamps).
   * @returns the created contact.
   */
  createContact(input: Omit<DJSEMCrmContact, "id" | "status" | "createdAt" | "updatedAt">): DJSEMCrmContact {
    return { ...input, id: `cc_${_seq.next()}`, status: "active", createdAt: AT, updatedAt: AT };
  }
  /**
   * Archive a CRM contact.
   * @param contact - the contact to archive.
   * @returns the archived contact.
   */
  archiveContact(contact: DJSEMCrmContact): DJSEMCrmContact {
    return { ...contact, status: "archived", updatedAt: AT };
  }
}

/** Manages booking records: creation, status transitions, prep-artifact linking, follow-ups, and readiness checks. */
export class DJSEMBookingService {
  /**
   * Create a new booking in "lead" status.
   * @param input - the booking's title/type/date/contacts/fee notes.
   * @returns the created booking record.
   */
  createBooking(input: CreateBookingInput): DJSEMBookingRecord {
    return {
      id: `bk_${_seq.next()}`, projectId: input.projectId, title: input.title,
      eventType: input.eventType, status: "lead", date: input.date,
      contactIds: input.contactIds ?? [], assetChecklist: [], followUpTasks: [],
      feeNotes: input.feeNotes, warnings: [],
      createdAt: AT, updatedAt: AT,
    };
  }

  /**
   * Move a booking to a new status if the transition is legal; otherwise records a warning and leaves status unchanged.
   * @param booking - the booking to transition.
   * @param newStatus - the desired new status.
   * @returns the updated booking.
   */
  changeStatus(booking: DJSEMBookingRecord, newStatus: DJSEMBookingStatus): DJSEMBookingRecord {
    const allowed = BOOKING_TRANSITIONS[booking.status] ?? [];
    if (!allowed.includes(newStatus)) return { ...booking, warnings: [...booking.warnings, `Cannot move from ${booking.status} to ${newStatus}`] };
    return { ...booking, status: newStatus, updatedAt: AT };
  }

  /**
   * Link an event brief to a booking.
   * @param booking - the booking to update.
   * @param eventBriefId - the event brief's id.
   * @returns the updated booking.
   */
  linkEventBrief(booking: DJSEMBookingRecord, eventBriefId: string): DJSEMBookingRecord {
    return { ...booking, eventBriefId, updatedAt: AT };
  }

  /**
   * Link a gig pack to a booking.
   * @param booking - the booking to update.
   * @param gigPackId - the gig pack's id.
   * @returns the updated booking.
   */
  linkGigPack(booking: DJSEMBookingRecord, gigPackId: string): DJSEMBookingRecord {
    return { ...booking, gigPackId, updatedAt: AT };
  }

  /**
   * Link a technical rider export to a booking.
   * @param booking - the booking to update.
   * @param technicalRiderExportId - the technical rider export's id.
   * @returns the updated booking.
   */
  linkTechnicalRider(booking: DJSEMBookingRecord, technicalRiderExportId: string): DJSEMBookingRecord {
    return { ...booking, technicalRiderExportId, updatedAt: AT };
  }

  /**
   * Append standard post-show follow-up tasks once a booking is completed (no-op otherwise).
   * @param booking - the booking to update.
   * @returns the updated booking (unchanged if not "completed").
   */
  createFollowUpTasks(booking: DJSEMBookingRecord): DJSEMBookingRecord {
    if (booking.status !== "completed") return booking;
    const tasks: DJSEMBookingFollowUpTask[] = [
      { id: `fu_${_seq.next()}`, title: "Send post-show recap", linkedResourceType: "post_show_recap", status: "todo", createdAt: AT },
      { id: `fu_${_seq.next()}`, title: "Thank promoter/venue", status: "todo", createdAt: AT },
      { id: `fu_${_seq.next()}`, title: "Update fan QR page to post-show", linkedResourceType: "fan_qr_page", status: "todo", createdAt: AT },
    ];
    return { ...booking, followUpTasks: [...booking.followUpTasks, ...tasks], updatedAt: AT };
  }

  /**
   * Check whether a booking is ready (has an event brief, no rejected checklist assets).
   * @param booking - the booking to validate.
   * @returns validation result.
   */
  validateReadiness(booking: DJSEMBookingRecord): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!booking.eventBriefId) issues.push({ path: "$.eventBriefId", message: "Event Brief missing" });
    if (booking.assetChecklist.some(a => a.status === "rejected")) issues.push({ path: "$.assetChecklist", message: "Rejected asset in checklist" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "booking_readiness");
  }
}

/** Manages venue CRM records. */
export class DJSEMVenueCrmService {
  /**
   * Create a new venue CRM record.
   * @param input - the venue's fields (excluding id/timestamps).
   * @returns the created venue CRM record.
   */
  createVenueCrmRecord(input: Omit<DJSEMVenueCrmRecord, "id" | "createdAt" | "updatedAt">): DJSEMVenueCrmRecord {
    return { ...input, id: `vcr_${_seq.next()}`, createdAt: AT, updatedAt: AT };
  }
  /**
   * Link a venue profile to a venue CRM record.
   * @param record - the venue CRM record to update.
   * @param venueProfileId - the venue profile's id.
   * @returns the updated venue CRM record.
   */
  linkVenueProfile(record: DJSEMVenueCrmRecord, venueProfileId: string): DJSEMVenueCrmRecord {
    return { ...record, venueProfileId, updatedAt: AT };
  }
}

// ============================================================================
// PROMPT 54 — Venue Profile Marketplace Expansion
// ============================================================================

/** A screen/display layout description for a venue marketplace pack. */
export interface DJSEMVenueScreenLayout {
  id: string; label: string;
  layoutType: "single_screen" | "dual_screen" | "triple_screen" | "led_wall" | "projection_mapping" | "custom";
  widthPx?: number; heightPx?: number; aspectRatio?: string;
  notes?: string; warnings: string[];
}

/** A venue's output/display profile, including operator/safe-mode requirements. */
export interface DJSEMVenueOutputProfile {
  id: string; label: string;
  outputType: string;
  safeModeRequired: boolean;
  operatorConfirmationRequired: boolean;
  warnings: string[];
}

/** An operator-facing note attached to a venue marketplace pack. */
export interface DJSEMVenueOperatorNote {
  id: string; title: string; body: string;
  severity: "info" | "warning" | "critical";
  category: string;
}

/** Rights-clearance summary for a venue marketplace pack's included media. */
export interface DJSEMVenuePackRightsSummary {
  allAssetsOwnedOrLicensed: boolean;
  containsThirdPartyVenueMedia: boolean;
  blockedReasons: string[];
  warnings: string[];
}

/** Safety-claim summary for a venue marketplace pack; hardware/laser certification claims are blocked. */
export interface DJSEMVenuePackSafetySummary {
  claimsHardwareCertification: boolean;
  containsLaserClaims: boolean;
  requiresOperatorVerification: boolean;
  containsPrivateCredentials: boolean;
  blockedReasons: string[];
  warnings: string[];
}

/** A publishable venue marketplace pack: screen layouts, output profiles, operator notes, and rights/safety summaries. */
export interface DJSEMVenueMarketplacePack {
  id: string;
  title: string; description: string;
  venueName?: string; city?: string; country?: string;
  screenLayouts: DJSEMVenueScreenLayout[];
  outputProfiles: DJSEMVenueOutputProfile[];
  operatorNotes: DJSEMVenueOperatorNote[];
  safeFallbackNotes: string[];
  rightsSummary: DJSEMVenuePackRightsSummary;
  safetySummary: DJSEMVenuePackSafetySummary;
  version: number;
  status: "draft" | "submitted_for_review" | "approved" | "published" | "rejected" | "suspended" | "archived";
  warnings: string[];
  createdAt: string; updatedAt: string;
}

/** Input for {@link VenueMarketplacePackService.createDraftPack}. */
export interface CreateVenuePackInput extends Omit<DJSEMVenueMarketplacePack, "id" | "status" | "version" | "createdAt" | "updatedAt"> {}

/** Manages venue marketplace pack lifecycle: draft, validation, review, publish, and project install. */
export class VenueMarketplacePackService {
  /**
   * Create a new draft venue marketplace pack at version 1.
   * @param input - the pack's fields (excluding id/status/version/timestamps).
   * @returns the created draft pack.
   */
  createDraftPack(input: CreateVenuePackInput): DJSEMVenueMarketplacePack {
    return { ...input, id: `vmp_${_seq.next()}`, status: "draft", version: 1, createdAt: AT, updatedAt: AT };
  }

  /**
   * Validate a pack against non-negotiable rules (no hardware/laser certification claims, no private credentials, rights owned/licensed, at least one screen layout).
   * @param pack - the pack to validate.
   * @returns validation result.
   */
  validatePack(pack: DJSEMVenueMarketplacePack): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!pack.title.trim()) issues.push({ path: "$.title", message: "required" });
    if (pack.safetySummary.claimsHardwareCertification) issues.push({ path: "$.safetySummary", message: "hardware certification claims are blocked" });
    if (pack.safetySummary.containsLaserClaims) issues.push({ path: "$.safetySummary", message: "laser safety claims are blocked" });
    if (pack.safetySummary.containsPrivateCredentials) issues.push({ path: "$.safetySummary", message: "private credentials are blocked" });
    if (!pack.rightsSummary.allAssetsOwnedOrLicensed) issues.push({ path: "$.rightsSummary", message: "all assets must be owned or licensed" });
    if (pack.screenLayouts.length === 0) issues.push({ path: "$.screenLayouts", message: "at least one screen layout required" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "venue_pack");
  }

  /**
   * Submit a pack for review if it passes validation.
   * @param pack - the pack to submit.
   * @returns the updated pack (unchanged if validation fails).
   */
  submitForReview(pack: DJSEMVenueMarketplacePack): DJSEMVenueMarketplacePack {
    const v = this.validatePack(pack);
    if (!v.ok) return pack;
    return { ...pack, status: "submitted_for_review", updatedAt: AT };
  }

  /**
   * Approve a pack that is currently under review.
   * @param pack - the pack to approve.
   * @returns the updated pack (unchanged if not "submitted_for_review").
   */
  approvePack(pack: DJSEMVenueMarketplacePack): DJSEMVenueMarketplacePack {
    if (pack.status !== "submitted_for_review") return pack;
    return { ...pack, status: "approved", updatedAt: AT };
  }

  /**
   * Publish an approved pack to the marketplace.
   * @param pack - the pack to publish.
   * @returns the updated pack (unchanged if not "approved").
   */
  publishPack(pack: DJSEMVenueMarketplacePack): DJSEMVenueMarketplacePack {
    if (pack.status !== "approved") return pack;
    return { ...pack, status: "published", updatedAt: AT };
  }

  /**
   * Install a published pack into a project, carrying a required operator-verification warning.
   * @param pack - the pack to install.
   * @returns the install record with source pack id/version and warnings.
   */
  installToProject(pack: DJSEMVenueMarketplacePack): { installedId: string; sourcePackId: string; sourceVersion: number; warnings: string[] } {
    return { installedId: `vpi_${_seq.next()}`, sourcePackId: pack.id, sourceVersion: pack.version, warnings: [...pack.warnings, "Operator verification required before use."] };
  }
}
